import React, { ChangeEvent, FC, useCallback, useState } from 'react';
import { FormText } from 'reactstrap';
import { useTranslation } from '../translations';
import { makeLazyComponent } from '../utils/lazy';

const PhoneInputInner = makeLazyComponent(
  async () => await import(/* webpackChunkName: "phoneinput" */ 'react-phone-input-2'),
);

interface PhoneInputProps {
  readonly name?: string;
  readonly disabled?: boolean;
  readonly value: string | null | undefined;
  readonly onChange?: (newValue: string) => void;
  readonly defaultCountry?: string;
  readonly id?: string;
}

function comparePhoneNumbers(a: string, b: string): boolean {
  return a.replaceAll(/[ ()-]/g, '') === b.replaceAll(/[ ()-]/g, '');
}

export const PhoneInput: FC<PhoneInputProps> = ({
  name,
  disabled,
  value,
  onChange,
  defaultCountry,
  id,
}) => {
  const { ts } = useTranslation();
  const [didFixCountryCode, setDidFixCountryCode] = useState(false);

  const onPhoneChange = useCallback(
    (
      originalInput: string,
      country: { countryCode: string },
      evt: ChangeEvent<HTMLInputElement>,
      formatted: string,
    ) => {
      const isChangeEvent = evt.type === 'change';

      /*
       * If the value was changed by the user, but was previously blank, or only contained the
       * default +1, we need to check if system assumed the country code from the first few digits
       * of the user input. If the formatted output doesn't match the user input, the system
       * dropped the default +1.
       *
       * This typically happens when the user enters a US phone number without the country code
       * through a password manager or autofill tool.
       */
      if (
        isChangeEvent &&
        (!value || value.length === 0 || value === '+1') &&
        country.countryCode !== 'us' &&
        originalInput.length === 10 &&
        !comparePhoneNumbers(originalInput, formatted)
      ) {
        setDidFixCountryCode(true);

        formatted = originalInput.startsWith('+') ? originalInput : `+1${originalInput}`;
      } else {
        setDidFixCountryCode(false);
      }

      onChange?.(
        !formatted || formatted.length === 0 || formatted === '+1'
          ? ''
          : formatted.replaceAll(/[ ()-]/g, ''),
      );
    },
    [onChange, value],
  );

  return (
    <>
      <PhoneInputInner
        country={defaultCountry?.toLowerCase() ?? 'us'}
        disabled={disabled}
        inputProps={{ name, id }}
        onChange={onPhoneChange}
        value={value}
      />
      {didFixCountryCode && (
        <FormText className="mt-1" color="info">
          {ts('phone_input.we_detected_a_usformatted_phone')}
        </FormText>
      )}
    </>
  );
};
